/**
 * WidgetClient — fetch wrapper dla widget API.
 *
 * Per docs/_archive/03-mvp-spec.md sek. 7.1, docs/_archive/07-mvp-minimal.md Chunk M.10 + M.11.
 *
 * Flow uwierzytelnienia (4 warstwy obrony per docs/07 sek. 6.4):
 *  1. X-Widget-Key header — identyfikuje tenant'a
 *  2. Origin — przeglądarka zarządza, backend sprawdza vs allowed_origins
 *  3. X-Session-Token — z bootstrap, HMAC sign'd z widget_key+origin+ip+expiry
 *  4. Rate limit per IP
 *
 * Client trzyma session_token w pamięci po bootstrap i dorzuca do każdego
 * następnego request. Przy 401 invalid_session_token → auto re-bootstrap +
 * retry one time (token mógł wygasnąć).
 */

import type { WidgetI18n } from '../i18n/messages.js';
import type { ConversationSource } from '../public-api.js';

/**
 * Typowany kontrakt tenant_configurations.ui_theme ↔ bootstrap payload.
 * Jedno miejsce z listą legalnych kluczy/wartości — wcześniej theme było
 * Record<string, string|null> i literówka klucza po stronie panelu/backendu
 * po cichu spadała do defaultów bez żadnego sygnału z kompilatora.
 * Brakujący/nieznany klucz w runtime nadal degraduje do defaultu.
 */
export type WidgetTheme = {
  primary_color?: string | null;
  accent_color?: string | null;
  /** Explicit font color on the primary/accent surface — overrides auto-contrast (inkFor). */
  primary_ink?: string | null;
  accent_ink?: string | null;
  widget_position?: 'left' | 'right' | null;
  color_scheme?: 'light' | 'dark' | null;
  entry_style?: 'bubble' | 'island' | null;
  launcher_display?: 'both' | 'desktop' | 'mobile' | 'hidden' | null;
  /** Mobilny odstęp launchera od dołu w px (0-200) — strony z dolnym menu barem. */
  launcher_offset_mobile?: number | null;
  launcher_icon_url?: string | null;
};

export type WidgetConfig = {
  tenant_slug: string;
  session_token: string;
  session_ttl_seconds: number;
  /** Tenant default UI language (2-letter) — drives the widget UI unless a `locale`
   * attribute overrides. The bundle ships no translations; strings come via `i18n`. */
  locale: string;
  /** Translation strings for the configured locale ONLY (server-provided). */
  i18n: WidgetI18n;
  bot: {
    name: string;
    avatar_url: string | null;
    greeting: string | null;
    suggested_questions: string[];
  };
  /** Tenant theme from ui_theme — see WidgetTheme for the typed contract. */
  theme: WidgetTheme;
  /** Whether to render the "Powered by …" footer (decided server-side). */
  branding?: { show: boolean };
  features: Record<string, unknown>;
  /** Whether the bot can take orders — gates the product-card "Dodaj do koszyka" action. */
  orders_enabled?: boolean;
  /**
   * Shop-cart mode (external_cart, XOR with orders_enabled): the widget performs
   * cart operations itself via a same-origin fetch to the shop's endpoint (the
   * visitor's own session cookies). enabled implies a non-empty endpoint_url.
   */
  external_cart?: {
    enabled: boolean;
    endpoint_url: string | null;
    cart_url: string | null;
    /** After a successful add the widget navigates to cart_url (panel toggle). */
    redirect_after_add?: boolean;
  };
  /**
   * Widget behaviour settings (panel „Widgety"). product_display_mode chooses the
   * product-card layout: 'card' (rich, default) | 'compact' (tight rows).
   */
  widgets?: {
    product_display_mode?: 'card' | 'compact';
    product_image_fit?: 'cover' | 'contain';
    variant_selector?: 'buttons' | 'dropdown';
  };
  visibility?: {
    /**
     * false → widget loader rezygnuje z renderowania DOM (zero śladu na stronie).
     * Konfigurowalne przez owner'a w panelu (Publish → Widget visibility).
     */
    enabled: boolean;
    /**
     * Lista glob-like patternów dopasowywanych do window.location.pathname.
     * Gdy któryś matchuje, widget się nie pokazuje na tej podstronie
     * (np. /checkout, /account/*). Pusta = brak ograniczeń.
     */
    hidden_url_patterns: string[];
  };
};

export type StartConversationResponse = {
  conversation_id: string;
  started_at: string;
};

export type StoredMessage = {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  products?: ProductCard[];
  cart_ops?: CartOp[] | null;
  created_at: string;
};

/** Cart-contents snapshot attached to a resolved 'view' op. `items` absent = shop doesn't expose contents; [] = empty cart. */
export type CartSnapshotItem = { sku: string; name?: string; qty?: number; line_total_cents?: number | null };
export type CartSnapshot = {
  count?: number;
  total_cents?: number | null;
  currency?: string;
  items?: CartSnapshotItem[];
};

/**
 * A shop-cart operation resolved server-side (SKUs from the catalog, never the
 * LLM). status 'pending' → the widget executes it ONCE against the shop endpoint
 * and reports the outcome; 'ok'/'error' → already resolved (history refetch).
 */
export type CartOp = {
  invocation_id: string;
  op: 'add' | 'remove' | 'clear' | 'view' | string;
  items: { sku: string; qty: number; name: string }[];
  status: 'pending' | 'ok' | 'error' | string;
  /** Business-level outcome layered on `status:'ok'` — shop declined the mutation. */
  result?: 'rejected' | string;
  /** Shop-provided reason, present only alongside `result:'rejected'`. */
  message?: string;
  cart?: CartSnapshot;
};

export type GetConversationResponse = {
  conversation_id: string;
  status: 'active' | 'closed' | string;
  messages: StoredMessage[];
  order?: OrderSnapshot | null;
  escalation?: EscalationSignal | null;
      contact?: ContactSignal | null;
};

export type ProductVariant = {
  variant_sku: string;
  label: string;
  axes: Record<string, string>;
  price_cents: number | null;
  available: boolean;
};

export type ProductCard = {
  product_id: string;
  name: string;
  /** Product-level SKU (variantless products) — the shop-cart key in external mode. */
  sku?: string;
  short_description: string;
  price_cents: number | null;
  currency: string;
  url: string;
  image_url: string;
  variants?: ProductVariant[];
};

export type OrderItem = {
  product_id: string;
  name: string;
  qty: number;
  unit_price_cents: number | null;
  line_total_cents: number | null;
  currency: string;
  /** Product/variant thumbnail frozen at add time; '' (or absent) → no image. */
  image_url?: string;
  // Wariant (spec 2026-06-05 §9): present only on variant lines. variant_label
  // ("Zielony / 10cm") is shown under the name; variant_sku keys the cart row so
  // two variants of one product are distinct lines.
  variant_sku?: string;
  variant_label?: string;
  /** Structured axes (key→value), e.g. {color:'czerwony', size:'10'}. Rendered as
   *  localized "Kolor: czerwony, Rozmiar: 10"; falls back to variant_label if absent. */
  variant_axes?: Record<string, string>;
};

export type OrderSnapshot = {
  status: 'draft' | 'submitted' | string;
  items: OrderItem[];
  total_cents: number;
  currency: string;
  customer: { name?: string; phone?: string; email?: string };
};

/**
 * Escalation signal — present (active=true) when the bot called escalate_to_human
 * on this conversation, telling the widget to render the contact form.
 * contact_submitted=true once the customer left their details (form then hidden,
 * survives reload). null = no open escalation.
 */
export type EscalationSignal = {
  active: boolean;
  contact_submitted: boolean;
  /** Marker of the latest escalate call — a change means the bot asked AGAIN. */
  requested_at?: string | null;
};

/**
 * Contact-request signal — present (active=true) when the bot needs the customer's
 * contact (e.g. a lead) and the single identity is incomplete. The widget renders
 * the contact form; once filled (server completes identity) the signal goes inactive.
 */
export type ContactSignal = {
  active: boolean;
  purpose: string;
  missing: string[];
  /** Current identity values — edit-mode prefill (empty missing = edit all fields). */
  prefill?: { name?: string | null; phone?: string | null; email?: string | null };
  /** Marker of the latest contact request — a change means the bot asked AGAIN. */
  requested_at?: string | null;
};

export type StreamEvent =
  | { type: 'start' }
  | { type: 'delta'; content: string }
  | { type: 'append'; content: string }
  | { type: 'replace'; content: string }
  | { type: 'tool_result'; content: string }
  | {
      // Sygnały formularzy TUŻ po wykonaniu narzędzi (serwer, I11 2026-09-01) —
      // zerwany stream nie gubi formularza, gdy lead/eskalacja już istnieją.
      type: 'signals';
      contact?: ContactSignal | null;
      escalation?: EscalationSignal | null;
    }
  | {
      type: 'done';
      message_id: string;
      products?: ProductCard[];
      order?: OrderSnapshot | null;
      cart_ops?: CartOp[] | null;
      escalation?: EscalationSignal | null;
      contact?: ContactSignal | null;
      created_at?: string;
    }
  | { type: 'error'; code: string; message: string };

export type AssistantMessageResponse = {
  message_id: string;
  role: 'assistant';
  content: string;
  guardrail_matched: string | null;
  products?: ProductCard[];
  order?: OrderSnapshot | null;
  cart_ops?: CartOp[] | null;
  escalation?: EscalationSignal | null;
      contact?: ContactSignal | null;
  created_at: string;
  finished_reason: 'ok' | 'killswitch' | 'budget_exceeded' | 'conversation_limit';
};

export type WidgetApiError = {
  code: string;
  message: string;
};

export class WidgetApiException extends Error {
  constructor(
    public readonly status: number,
    public readonly errors: WidgetApiError[],
  ) {
    super(errors[0]?.message ?? `Widget API error (${status})`);
    this.name = 'WidgetApiException';
  }
}

function isSessionExpiredError(err: unknown): err is WidgetApiException {
  if (!(err instanceof WidgetApiException) || err.status !== 401) {
    return false;
  }
  return err.errors.some((e) => e.code === 'invalid_session_token' || e.code === 'missing_session_token');
}

/**
 * fetch() wrapper that maps network errors (no response, DNS, CORS preflight
 * abort) to WidgetApiException(0, network_error). Callers only need to handle
 * the typed exception path.
 */
async function fetchOrThrow(url: string, init: RequestInit): Promise<Response> {
  try {
    return await fetch(url, init);
  } catch (err) {
    throw new WidgetApiException(0, [
      { code: 'network_error', message: err instanceof Error ? err.message : 'Network error' },
    ]);
  }
}

export class WidgetClient {
  private sessionToken: string | null = null;

  constructor(
    private readonly baseUrl: string,
    private readonly widgetKey: string,
  ) {}

  async bootstrap(): Promise<WidgetConfig> {
    const data = await this.requestJson<{ data: WidgetConfig }>('POST', '/bootstrap', {}, { withSession: false });
    this.sessionToken = data.data.session_token;
    return data.data;
  }

  async startConversation(
    sourceUrl?: string,
    customer?: { name?: string; phone?: string; email?: string },
    source?: ConversationSource,
  ): Promise<StartConversationResponse> {
    const body: Record<string, unknown> = {};
    if (sourceUrl) body.source_url = sourceUrl;
    // Prefill zalogowanego klienta sklepu — server waliduje per-pole
    // (telefon/email) i trzyma na rozmowie; pierwszy koszyk go dziedziczy.
    if (customer && Object.values(customer).some((v) => typeof v === 'string' && v.trim() !== '')) {
      body.customer = customer;
    }
    // Where the widget was opened from (<{brand}-search>, a future CTA element…) —
    // undefined (plain host embed / manual open) omits the field entirely.
    if (source) body.source = source;
    const data = await this.requestJson<{ data: StartConversationResponse }>('POST', '/conversations', body);
    return data.data;
  }

  async sendMessage(conversationId: string, message: string): Promise<AssistantMessageResponse> {
    const data = await this.requestJson<{ data: AssistantMessageResponse }>(
      'POST',
      `/conversations/${encodeURIComponent(conversationId)}/messages`,
      { message },
    );
    return data.data;
  }

  /**
   * Streaming variant — server emituje SSE z `data: {json}\n\n` linijkami.
   * Każda linia ma `type`: start/delta/append/replace/tool_result/done/error.
   * Callback wywoływany dla każdego eventu — pozwala UI zbierać delta + render.
   *
   * Auto-rebootstrap przy 401 invalid_session_token (parytet z requestJson):
   * token ma krótki TTL (~15 min), a rozmowa bywa dłuższa — bez tego stream
   * 401'ował w POŁOWIE rozmowy i wymagał ręcznego odświeżenia strony. 401 leci
   * PRZED jakimkolwiek eventem SSE (sprawdzane na `!response.ok`), więc ponowienie
   * nie podwaja delt.
   *
   * 404 (streaming disabled lub conversation not found) → throw — caller
   * fallbackuje na non-stream sendMessage.
   */
  async streamMessage(
    conversationId: string,
    message: string,
    onEvent: (event: StreamEvent) => void,
  ): Promise<void> {
    await this.reauthAndRetry(() => this.rawStream(conversationId, message, onEvent));
  }

  private async rawStream(
    conversationId: string,
    message: string,
    onEvent: (event: StreamEvent) => void,
  ): Promise<void> {
    const url = `${this.baseUrl}/conversations/${encodeURIComponent(conversationId)}/messages/stream`;
    const headers = this.buildHeaders('text/event-stream');

    const response = await fetchOrThrow(url, {
      method: 'POST',
      headers,
      body: JSON.stringify({ message }),
      mode: 'cors',
    });

    if (!response.ok) {
      const payload = await response.json().catch(() => ({}));
      const errors = (payload as { errors?: WidgetApiError[] }).errors ?? [
        { code: 'http_error', message: `HTTP ${response.status}` },
      ];
      throw new WidgetApiException(response.status, errors);
    }

    if (!response.body) {
      throw new WidgetApiException(0, [{ code: 'no_body', message: 'Empty response body' }]);
    }

    const reader = response.body.getReader();
    const decoder = new TextDecoder('utf-8');
    let buffer = '';

    while (true) {
      const { value, done } = await reader.read();
      if (done) break;
      buffer += decoder.decode(value, { stream: true });

      // SSE separator = "\n\n". Process complete frames, keep partial in buffer.
      let sepIdx;
      while ((sepIdx = buffer.indexOf('\n\n')) !== -1) {
        const rawFrame = buffer.slice(0, sepIdx);
        buffer = buffer.slice(sepIdx + 2);

        // Each frame may have multiple lines (data: ...). Pick the data lines.
        for (const line of rawFrame.split('\n')) {
          if (!line.startsWith('data: ')) continue;
          const json = line.slice(6);
          try {
            const evt = JSON.parse(json) as StreamEvent;
            onEvent(evt);
          } catch {
            // ignore malformed frame
          }
        }
      }
    }
  }

  async getConversation(conversationId: string): Promise<GetConversationResponse> {
    const data = await this.requestJson<{ data: GetConversationResponse }>(
      'GET',
      `/conversations/${encodeURIComponent(conversationId)}`,
      {},
    );
    return data.data;
  }

  /**
   * Submit order via inline form — bypasses LLM, persists fields directly
   * on current draft Order, fires OrderSubmissionService. Server-side phone
   * validation; on reject returns 422 with code='invalid_phone'.
   */
  async submitOrderForm(
    conversationId: string,
    payload: { name: string; phone: string; email?: string; notes?: string },
  ): Promise<{ confirmation: string; order: OrderSnapshot | null }> {
    const body: Record<string, unknown> = { ...payload };
    const data = await this.requestJson<{ data: { confirmation: string; order: OrderSnapshot | null } }>(
      'POST',
      `/conversations/${encodeURIComponent(conversationId)}/orders/submit`,
      body,
    );
    return data.data;
  }

  /**
   * Submit the escalation contact form — attaches the customer's contact to the
   * open escalation (the bot called escalate_to_human) and notifies the owner.
   * Phone is optional; if present the server validates it (422 code='invalid_phone').
   * 409 code='escalation_not_active' when there's no open escalation.
   */
  async submitEscalation(
    conversationId: string,
    payload: { name: string; email: string; phone?: string; message: string },
  ): Promise<{ confirmation: string; escalation: EscalationSignal | null }> {
    const body: Record<string, unknown> = { ...payload };
    const data = await this.requestJson<{ data: { confirmation: string; escalation: EscalationSignal | null } }>(
      'POST',
      `/conversations/${encodeURIComponent(conversationId)}/escalations/submit`,
      body,
    );
    return data.data;
  }

  /** Submit the contact form (np. leady): writes the single identity + completes the
   *  lead (purpose='lead'). All three fields required; server validates with tenant dial code. */
  async submitContact(
    conversationId: string,
    payload: { name: string; phone: string; email: string; purpose: string },
  ): Promise<{ confirmation: string; contact: ContactSignal | null }> {
    const data = await this.requestJson<{ data: { confirmation: string; contact: ContactSignal | null } }>(
      'POST',
      `/conversations/${encodeURIComponent(conversationId)}/contact/submit`,
      { ...payload },
    );
    return data.data;
  }

  /**
   * Add a product to the draft cart from a one-click product-card action. Not a bot
   * turn (no LLM): the server adds it (increment) + appends a "Dodano X" breadcrumb
   * message. Returns the updated cart snapshot + the new message so the widget updates
   * the cart bar + transcript in place.
   */
  async addToCart(
    conversationId: string,
    productId: string,
    variantSku?: string,
  ): Promise<{ order: OrderSnapshot | null; message: StoredMessage }> {
    const body: Record<string, unknown> = { product_id: productId };
    if (variantSku) {
      body.variant_sku = variantSku;
    }
    const data = await this.requestJson<{ data: { order: OrderSnapshot | null; message: StoredMessage } }>(
      'POST',
      `/conversations/${encodeURIComponent(conversationId)}/cart`,
      body,
    );
    return data.data;
  }

  /**
   * Report the outcome of a shop-cart operation (external_cart mode) so the bot
   * learns the result next turn (server appends a templated breadcrumb).
   * Fire-and-forget semantics; `keepalive` lets the POST survive a page
   * navigation started right after the shop fetch.
   */
  async postExternalCartEvent(
    conversationId: string,
    payload:
      | {
          invocation_id: string;
          status: 'ok' | 'error';
          result?: 'rejected';
          message?: string;
          cart?: CartSnapshot;
        }
      | { op: 'add'; items: { sku: string; qty: number }[]; status: 'ok' | 'error'; result?: 'rejected'; message?: string },
  ): Promise<{ message?: StoredMessage } | null> {
    const url = `${this.baseUrl}/conversations/${encodeURIComponent(conversationId)}/external-cart-event`;
    try {
      const response = await fetchOrThrow(url, {
        method: 'POST',
        headers: this.buildHeaders('application/json'),
        body: JSON.stringify(payload),
        mode: 'cors',
        keepalive: true,
      });
      const body = (await response.json().catch(() => null)) as
        | { data?: { message?: StoredMessage } }
        | null;
      return body?.data ?? null;
    } catch {
      // Best-effort: zgubiony event = bot nie zna wyniku (chip i tak pokazał
      // prawdę klientowi); nie przerywamy UX błędem raportowania.
      return null;
    }
  }

  /**
   * Wykonuje request z auto-rebootstrap przy 401 invalid_session_token.
   * Zapobiega cichym failures po wygaśnięciu tokena (TTL 15 min default).
   */
  private async requestJson<T>(
    method: string,
    path: string,
    body: Record<string, unknown>,
    options: { withSession?: boolean } = {},
  ): Promise<T> {
    const withSession = options.withSession ?? true;

    return this.reauthAndRetry(() => this.rawRequest<T>(method, path, body, withSession), withSession);
  }

  /**
   * Wspólna polityka odzyskiwania sesji dla JSON i SSE: uruchom `run`, a gdy
   * poleci 401 invalid/missing_session_token — re-bootstrapuj raz (świeży token)
   * i ponów. `canReauth=false` dla samego /bootstrap (brak tokena do odnowienia).
   * Jedno miejsce, żeby ścieżka JSON i strumieniowa nie rozjechały się.
   */
  private async reauthAndRetry<T>(run: () => Promise<T>, canReauth = true): Promise<T> {
    try {
      return await run();
    } catch (err) {
      if (!canReauth || !isSessionExpiredError(err)) {
        throw err;
      }
      const res = await this.rawRequest<{ data: WidgetConfig }>('POST', '/bootstrap', {}, false);
      this.sessionToken = res.data.session_token;
      return await run();
    }
  }

  /**
   * Build request headers. `accept` varies (JSON vs SSE); the session token is
   * attached when present and not explicitly opted out (bootstrap pre-auth).
   */
  private buildHeaders(accept: string, withSession = true): Record<string, string> {
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      Accept: accept,
      'X-Widget-Key': this.widgetKey,
    };
    if (withSession && this.sessionToken) {
      headers['X-Session-Token'] = this.sessionToken;
    }
    return headers;
  }

  private async rawRequest<T>(
    method: string,
    path: string,
    body: Record<string, unknown>,
    withSession: boolean,
  ): Promise<T> {
    const url = `${this.baseUrl}${path}`;
    const headers = this.buildHeaders('application/json', withSession);

    const response = await fetchOrThrow(url, {
      method,
      headers,
      body: method === 'GET' ? undefined : JSON.stringify(body),
      mode: 'cors',
    });

    const payload = (await response.json().catch(() => ({}))) as
      | { data?: unknown; errors?: WidgetApiError[] }
      | undefined;

    if (!response.ok) {
      throw new WidgetApiException(response.status, payload?.errors ?? [
        { code: 'http_error', message: `HTTP ${response.status}` },
      ]);
    }

    return payload as T;
  }
}
